import React from 'react';
import { Link } from 'react-router-dom';

// Hooks
import { useAlertRuleForm } from '../../../hooks/useAlertRuleForm';

// Types
import AlertRules from "../../../types/Alerts/AlertRules";
import InstanceTarget from "../../../types/instance/InstanceTarget";
import HostTarget from "../../../types/host/HostTarget";

// Constants
import { DATA_LOADED, generalErrorMessage } from '../../../component/Constants';

// Helpers
import ConditionalRender from "../../../helpers/ConditionalRender";

//Components
import Alert from "../../../component/Alert";
import Scope from "../../../component/scope/";
import Metric from "../../../component/metrics/";
import { AlertRuleFormUtils } from '../../../helpers/AlertRuleFormValidation';

function Detail(props: {
    alertRules: AlertRules[],
    refresh: Function,
    instances: InstanceTarget[],
    hosts: HostTarget[],
    loading: number
}) {
    const {
        formState,
        action,
        updateFormData,
        validateName,
        validateThreshold,
        validateStatistic,
        handleSubmit,
        resetFieldsForType,
        sortedInstances,
        sortedHosts
    } = useAlertRuleForm({
        alertRules: props.alertRules,
        instances: props.instances,
        hosts: props.hosts,
        loading: props.loading,
        refresh: props.refresh
    });

    const { formData, validation, uiState } = formState;
    const isDelete = action.type === 'delete';

    // Build type string from entity and metric type
    const typeValue = AlertRuleFormUtils.buildTypeString(formData.entitytype, formData.metrictype);

    return (
            <div className="col">
                {props.loading === DATA_LOADED &&
                    <div className="card">
                        <div className="card-header">
                            <i className="fal fa-exclamation-triangle fa-fw mr-10" aria-hidden="true"/>
                            {action.title} Alert Rule
                        </div>
                        <div className="card-body">

                            {uiState.isMissing ? (
                                    <React.Fragment>
                                        <Alert message="The requested alert rule is either invalid or no longer available."
                                               heading="Invalid request" variant="alert-danger"/>
                                        <div className="row g-0 form">
                                            <div className="col-xs-12 col-md-9 col-lg-6 actions">
                                                <Link to="/admin/alert-rules/" role="button" className="btn btn-secondary"><i
                                                        className="fal fa-arrow-circle-left fa-fw fa-sm"/><span>Back</span></Link>
                                            </div>
                                        </div>
                                    </React.Fragment>
                            ) : (
                                    <form onSubmit={handleSubmit}>

                                        {(!validation.isFormValid || validation.statistic.hasError) && (
                                                <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                                       variant="alert-danger"/>
                                        )}

                                        <p>Please complete the required details below:</p>

                                        <div className="row g-0 form">

                                            {/* Name */}
                                            <div className="col-xs-12 col-md-3">
                                        <span className="required">
                                            <label htmlFor="name" className="reminder">Alert rule name</label>
                                        </span>
                                            </div>

                                            <div className="col-xs-12 col-md-6 col-lg-4">
                                                <input
                                                        type="text"
                                                        id="name"
                                                        maxLength={250}
                                                        value={formData.name}
                                                        onChange={(e) => updateFormData({ name: e.target.value })}
                                                        onBlur={validateName}
                                                        className={`form-control ${validation.name.className}`}
                                                        disabled={uiState.disabled}
                                                        title={validation.name.isUsed ? "Alert rule name already used." : undefined}
                                                        data-lpignore={true}
                                                />
                                                <small className="text-danger">{validation.name.isUsed ? "Alert rule name already used." : ""}</small>
                                            </div>
                                            <div className="w-100"/>

                                            {/* Alert type */}
                                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="type" className="reminder">Alert type</label>
                                </span>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <select
                                                        id="type"
                                                        autoFocus
                                                        className={`form-control`}
                                                        onChange={e => resetFieldsForType(e.target.value)}
                                                        value={typeValue}
                                                        disabled={uiState.disabled}
                                                        data-lpignore={true}
                                                >
                                                    <option value='host_statistic'>Host Statistic</option>
                                                    <option value='datasource_statistic'>Instance Statistic</option>
                                                    <option value='datasource_activity'>Instance Activity</option>
                                                </select>
                                            </div>
                                            <div className="w-100"/>

                                            {/* Scope */}
                                            <Scope entityType={formData.entitytype}
                                                   hostId={formData.hostid}
                                                   setHostId={(value: number | string) => updateFormData({ hostid: value })}
                                                   datasourceId={formData.datasourceid}
                                                   setStatistic={(value: string) => updateFormData({ statistic: value })}
                                                   setDatasourceId={(value: number | string) => updateFormData({ datasourceid: value })}
                                                   hosts={sortedHosts}
                                                   instances={sortedInstances}
                                                   disabled={uiState.disabled}
                                            />

                                            {/* Metric */}
                                            <Metric entityType={formData.entitytype}
                                                    metricType={formData.metrictype}
                                                    hostId={formData.hostid}
                                                    datasourceId={formData.datasourceid}
                                                    disabled={uiState.disabled}
                                                    setAggregation={(value: string) => updateFormData({ aggregation: value })}
                                                    aggregation={formData.aggregation}
                                                    setStatistic={(value: string) => updateFormData({ statistic: value })}
                                                    statisticError={validation.statistic.hasError ? 'invalid' : ''}
                                                    statistic={formData.statistic}
                                                    isDelete={isDelete}
                                                    hosts={sortedHosts}
                                                    instances={sortedInstances}
                                            />

                                            {/* Threshold */}
                                            <div className="col-xs-12 col-md-3">
                                        <span className="required">
                                            <label htmlFor="type" className="reminder">Threshold</label>
                                        </span>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-4 ">
                                                <div className=" d-flex justify-content-between">
                                                    <select
                                                            id="decreasing"
                                                            autoFocus
                                                            value={formData.decreasing}
                                                            onChange={(e) => updateFormData({ decreasing: e.target.value })}
                                                            className={`form-control w-25 mr-10`}
                                                            disabled={uiState.disabled}
                                                            data-lpignore={true}
                                                    >
                                                        <option value='false'>{'>='}</option>
                                                        <option value='true'>{'<='}</option>
                                                    </select>
                                                    <input
                                                            type="number"
                                                            id="threshold"
                                                            maxLength={250}
                                                            value={formData.threshold || ''}
                                                            onChange={(e) => updateFormData({ threshold: Number(e.target.value) })}
                                                            onBlur={validateThreshold}
                                                            className={`form-control w-50 mr-10 no-arrows ${validation.threshold.className}`}
                                                            style={{ width: '45%' }}
                                                            disabled={uiState.disabled}
                                                            title={validation.threshold.message}
                                                            data-lpignore={true}
                                                    />

                                                    <select
                                                            id="units"
                                                            autoFocus
                                                            className={`form-control w-25`}
                                                            value={formData.units}
                                                            onChange={(e) => updateFormData({ units: e.target.value })}
                                                            disabled={uiState.disabled}
                                                            data-lpignore={true}
                                                    >
                                                        <option value=''>&nbsp;</option>
                                                        <option value='%'>%</option>
                                                        <option value='ms'>ms</option>
                                                    </select>
                                                </div>
                                                <ConditionalRender if={validation.threshold.message}>
                                                    <small className="text-danger mid-error">{validation.threshold.message}</small>
                                                </ConditionalRender>
                                            </div>
                                            <div className="w-100"/>

                                            {/*Rule evaluation frequency (mins)*/}
                                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="frequency"
                                           className="reminder">Rule evaluation frequency (mins)</label>
                                </span>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3 ">
                                                <div className='w-25'>
                                                    <select
                                                            id="frequency"
                                                            autoFocus
                                                            className={`form-control`}
                                                            value={1}
                                                            disabled={true}
                                                            data-lpignore={true}
                                                    >
                                                        <option value='1'>1</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="w-100"/>

                                            {/*Evaluation window (mins)*/}
                                            <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="evaluation" className="reminder">Evaluation window (mins)</label>
                                </span>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <div className='w-25'>
                                                    <select
                                                            id="evaluation"
                                                            autoFocus
                                                            className={`form-control`}
                                                            value={1}
                                                            disabled={true}
                                                            data-lpignore={true}
                                                    >
                                                        <option value='1'>1</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="w-100"/>


                                            {/* Actions */}
                                            {/*<div className="col-xs-12 col-md-3">*/}
                                            {/*    <label className="reminder">Action (s)</label>*/}
                                            {/*</div>*/}
                                            {/*<div className="col-xs-12 col-md-6 col-lg-3">*/}
                                            {/*    <MultiSelect value={actions} options={alertActions} showSelectAll={false}*/}
                                            {/*                 onChange={(e) => setActions(e.value)}*/}
                                            {/*                 optionLabel="name" display="chip"/>*/}

                                            {/*</div>*/}
                                            {/*<div className="w-100"/>*/}

                                            {/* enabled */}
                                            <div className="col-xs-12 col-md-3">
                                                <label className="reminder">Rule enabled</label>
                                            </div>
                                            <div className="col-xs-12 col-md-6 col-lg-3">
                                                <div className='form-check form-switch'>
                                                    <input
                                                            type="checkbox"
                                                            id="disabled"
                                                            className="form-check-input"
                                                            disabled={uiState.disabled}
                                                            onChange={() => updateFormData({ disabled: !formData.disabled })}
                                                            checked={!formData.disabled}
                                                            data-lpignore={true}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-100"/>

                                            {/* Form Actions */}
                                            <div className="col-12 actions">
                                                <button onClick={() => window.history.back()} type="button" className="btn btn-secondary"><i
                                                        className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></button>
                                                <button
                                                        onClick={(e) => {
                                                            validateStatistic();
                                                            handleSubmit(e);
                                                        }}
                                                        type='submit'
                                                        disabled={uiState.loading}
                                                        className={`btn btn-${action.variant}`}>
                                                    <i className={`${action.icon} fa-sm`}/><span>{action.title}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                            )}
                        </div>
                    </div>
                }
            </div>
    )
}

export default Detail;
